import React, { useState, FC, useRef, RefObject, useEffect, useMemo } from 'react';
import {
    Button,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Alert, AlertTitle
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TimekeeperService } from 'services/TimekeeperService';
import {
    LimitFormValues, defaultLimitValues, TipoCheckLimitEnum,
    createLimitSchema, buildLimitPayload
} from './limitFormSchema';
import { LimitFormFields } from './LimitFormFields';

interface UpdateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    rowToEdit: any;
    reloading: () => void;
    cleanSelected: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

const UpdateLimits: FC<UpdateProps> = ({ open, setOpen, rows, rowToEdit, reloading, cleanSelected }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();

    const [datePatternOptions, setDatePatternOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)

    // Schema con validaciones dinámicas
    const schema = useMemo(() => createLimitSchema(t, rows, rowToEdit?.id), [t, rows, rowToEdit?.id]);

    const { control, register, reset, handleSubmit, watch, formState: { errors, isDirty } } = useForm<LimitFormValues>({
        defaultValues: defaultLimitValues,
        resolver: zodResolver(schema),
        mode: 'onChange'
    });

    const nameValue = watch('name');
    const descriptionValue = watch('description');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const id: number = rowToEdit.id;
                const [datePatterns, limit] = await Promise.allSettled([
                    timekeeperService.date_pattern.getAll(),
                    timekeeperService.limits.getOne(id)
                ])

                if (limit.status === 'fulfilled' && limit.value) {
                    const limitData = limit.value;

                    reset({
                        name: limitData.name,
                        description: limitData.description || '',
                        type_limit: limitData.type_limit,
                        modify_by_etc: limitData.modify_by_etc,
                        type_check_limit: (limitData as any).type_check_limit ?? TipoCheckLimitEnum.TEMPORADA,
                        type_shift_limit: (limitData as any).type_shift_limit ?? null,
                        start_month: (limitData as any).start_month ?? null,
                        start_day: (limitData as any).start_day ?? null,
                        end_month: (limitData as any).end_month ?? null,
                        end_day: (limitData as any).end_day ?? null,
                        frecuency: (limitData as any).frecuency ?? null,
                        type_unit_time: (limitData as any).type_unit_time ?? null,
                        date_pattern_id: (limitData as any).date_pattern?.date_pattern_id ?? null,
                    });
                }

                if (datePatterns.status === 'fulfilled' && datePatterns.value) {
                    setDatePatternOptions(datePatterns.value);
                }
            } catch (error) {
                console.error('Error fetching limit data or date patterns:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && rowToEdit) {
            fetchData();
        }
    }, [open, rowToEdit, reset]);

    const changes = () => {
        if (isDirty) {
            setAlerts(true)
        } else {
            setOpen(false);
        }
    }

    const closeForm = () => {
        reset(defaultLimitValues);
        setOpen(false);
        setAlerts(false);
        setLoading(false);
        cleanSelected();
    }

        const onSubmit = async (data: LimitFormValues) => {
        setLoading(true);

        try {
            await timekeeperService.limits.update(rowToEdit.id, buildLimitPayload(data));
            enqueueSnackbar(t('accruals.limits.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();
        } catch (error) {
            console.error('Error updating limit:', error);
            enqueueSnackbar(t('accruals.limits.dialog.alerts.add.error'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.limits.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <LimitFormFields
                    control={control}
                    register={register}
                    errors={errors}
                    loading={loading}
                    nameValue={nameValue}
                    descriptionValue={descriptionValue}
                    datePatternOptions={datePatternOptions}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={loading || !isDirty} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { UpdateLimits };